import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  InputBase,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  DarkMode as DarkModeIcon,
  Notifications as NotificationsIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChangePasswordDialog from './ChangePasswordDialog';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', path: '/', color: '#3b82f6', tabKey: 'dashboard' },
  { text: 'Quản lý tài khoản', path: '/accounts', color: '#06b6d4', adminOnly: true },
  { text: 'Khách hàng', path: '/customers', color: '#ec4899', adminOnly: true },
  { text: 'Hiệu suất Master', path: '/performance', color: '#10b981', tabKey: 'master-performance' },
  { text: 'Báo cáo', path: '/reports', color: '#a78bfa', tabKey: 'reports' },
  { text: 'Thông báo', path: '/notifications', color: '#f59e0b', hideForViewer: true },
];

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const { user, logout, isAdmin, isViewer, canAccessTab } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', py: 1 }}>
      {/* Logo */}
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.1rem', lineHeight: 1 }}>
            M
          </Typography>
        </Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: 'white', letterSpacing: '-0.3px', fontSize: '1.05rem' }}
        >
          MT5 Dashboard
        </Typography>
      </Box>

      {/* Nav items */}
      <List sx={{ px: 1.5, flex: 1 }}>
        {menuItems.map((item) => {
          if (item.adminOnly && !isAdmin()) return null;
          if (item.hideForViewer && isViewer()) return null;
          if (item.tabKey && isViewer() && !canAccessTab(item.tabKey)) return null;
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: '10px',
                  px: 2,
                  py: 1.1,
                  backgroundColor: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                }}
              >
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    mr: 2,
                    flexShrink: 0,
                    boxShadow: `0 0 8px ${item.color}80`,
                  }}
                />
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.88rem',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'white' : 'rgba(255,255,255,0.6)',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* User section at bottom */}
      <Box
        sx={{
          mx: 1.5,
          mb: 1,
          p: 1.5,
          borderRadius: '10px',
          backgroundColor: 'rgba(255,255,255,0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            fontSize: '0.8rem',
            fontWeight: 700,
          }}
        >
          {user?.username?.[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'white', lineHeight: 1.2 }}>
            {user?.username}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', textTransform: 'capitalize' }}>
            {user?.role}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', overflowX: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ gap: 1, minHeight: '60px !important' }}>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ color: 'text.secondary', display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'flex' } }}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          {/* Search bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '10px',
              px: 1.5,
              py: 0.6,
              flex: 1,
              maxWidth: 380,
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1rem' }} />
            <InputBase
              placeholder="Search..."
              sx={{ color: 'text.primary', fontSize: '0.875rem', flex: 1 }}
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Right icons */}
          <Tooltip title="Dark mode">
            <IconButton size="small" sx={{ color: 'text.secondary' }}>
              <DarkModeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Language">
            <IconButton size="small" sx={{ color: 'text.secondary' }}>
              <TranslateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notifications">
            <IconButton size="small" sx={{ color: 'text.secondary' }}>
              <NotificationsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Avatar */}
          <Tooltip title={user?.username}>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                }}
              >
                {user?.username?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { mt: 1 } }}
          >
            <MenuItem onClick={() => { setAnchorEl(null); setPasswordDialogOpen(true); }}>
              Đổi mật khẩu
            </MenuItem>
            <MenuItem onClick={handleLogout}>Đăng xuất</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2.5, md: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100vw',
          overflowX: 'hidden',
          mt: '60px',
        }}
      >
        <Outlet />
      </Box>

      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
      />
    </Box>
  );
}
